
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.3.0
 * @copyright  Copyright (C) 2024-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2024-08-23 (1.0.0) (jhoward)
 *    - Refactor textual default section from <textual-menu> to <default-textual-section>.
 *
 *  2024-08-26 (1.1.0) (jhoward)
 *    - Handle selected menu items via store in <default-textual-section>.
 *    - Add scoped CSS in <default-textual-section>.
 *    - Customize item grid in <default-textual-section> based on rows in each item.
 *
 *  2024-12-03 (1.1.1) (jhoward)
 *    - Add isCartEnabled prop to <default-textual-section>.
 *    - Do not show "favorite" icon when isCartEnabled prop is set to false in <default-textual-section>.
 *
 *  2025-02-11 (1.2.0) (jhoward)
 *    - Add <menu-schedules> to the <defaultTextualSection> component if in admin view.
 *
 *  2025-04-08 (1.3.0) (jhoward)
 *    - Add "menuItemComponent" prop to <defaultTextualSection>.
 *    - Add "menuItemTagLocation" prop to <defaultTextualSection>.
 *    - Add "contentAreaTopLayout" prop to <defaultTextualSection>.
 *    - Add "contentAreaBottomLayout" prop to <defaultTextualSection>.
 *    - Add "isTagLegendHidden" prop to <defaultTextualSection>.
 *    - Add "isTitleBoxVisible" prop to <defaultTextualSection>.
 *    - Add "isTitleBufferVisible" prop to <defaultTextualSection>.
 *    - Add "sectionModifier" prop to <defaultTextualSection>.
 *    - Add "sectionMenuType" prop to <defaultTextualSection>.
 *    - Auto-detect long price strings and adjust UI accordingly in <defaultTextualSection>.
 *    - Remove "textual_" prefix from class names in <defaultTextualSection>.
 *    - Add "sectionTitleImage" slot to facilitate showing an image above the title in <defaultTextualSection>.
 *    - Add "beforeTopContentArea" slot to facilitate showing content above the top content area in <defaultTextualSection>.
 *    - Add "beforeGroupTitle" slot to facilitate showing a tag before the title in <defaultTextualSection>.
 *    - Add section title DOM elements to match <defaultVisualSection> in <defaultTextualSection>.
 *    - Use <section-item-subgroup> to display menu items in <defaultTextualSection>.
 *    - Remove handing of "maxItemRows" in <defaultTextualSection>.
 */

import {defineComponent, PropType} from 'vue'
import MenuTagLegend from "../../menu-tag-legend.vue";
import MenuTag from "../../menu-tag.vue";
import {
  ALLOWED_ITEM_COMPONENTS,
  Menu, MENU_ITEM_TAG_LOCATIONS,
  MenuSection, SECTION_CONTENT_AREA_BOTTOM_LAYOUT, SECTION_CONTENT_AREA_TOP_LAYOUT,
  WINE_LIST_ITEM_COMPONENTS
} from "../../defs";
import {
  computedIsScheduled,
  computeItemGroupPricingTypes,
  isNormalizedScheduleActive, methodGetPreviousGroupAndSubgroupTitles, methodIsSubgroupPricingShown
} from "../../../../helpers/menu-sections";
import MenuVersion from "../../menu-version.vue";
import MenuSchedules from "../../schedule/MenuSchedules.vue";
import SectionItemSubgroup from "../SectionItemSubgroup.vue";

export default defineComponent({
  name: "defaultTextualSection",
  components: {
    SectionItemSubgroup, MenuSchedules,
    MenuVersion, MenuTag, MenuTagLegend,
  },
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu | null>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    },
    contentAreaTopLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_TOP_LAYOUT>,
      default: ''
    },
    contentAreaBottomLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_BOTTOM_LAYOUT>,
      default: ''
    },
    isTagLegendHidden: {
      type: Boolean,
      default: false
    },
    isTitleBoxVisible: {
      type: Boolean,
      default: false
    },
    isTitleBufferVisible: {
      type: Boolean,
      default: false
    },
    sectionModifier: {
      type: String,
      default: ''
    },
    sectionMenuType: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      longPriceThreshold: 20,

      WINE_LIST_ITEM_COMPONENTS,
      ALLOWED_ITEM_COMPONENTS,
    };
  },
  computed: {
    isScheduled: computedIsScheduled,
    itemGroupPricingTypes: computeItemGroupPricingTypes,
    isEmpty(): boolean {
      for (let groupTitle in this.section.grouped_items) {
        for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
          for (let i = 0, l = this.section.grouped_items[groupTitle][subgroupTitle].length || 0; i < l; i++) {
            if (0 === this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules.length) {
              return false;
            }
            for (let ii = 0, ll = this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules.length || 0; ii < ll; ii++) {
              if (isNormalizedScheduleActive(this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules[ii], this.$store.state.currentDateTimeUtc, this.$store.state.currentDayOfWeek, this.$store.state.currentTimeOfDay)) {
                return false;
              }
            }
          }
        }
      }

      return true;
    },
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--section-show_bin': !!this.section.ms_show_bin,
        'menu--section-show_pricing_header': !!this.section.ms_show_pricing_header,
        'menu--section-group_by_price': !!this.section.ms_group_by_price,
        'menu--section-highlight': !!this.section.ms_highlight,
        'menu--section-desserts': this.section.ms_title.toLowerCase() === 'desserts',
        'menu--section-long_price': this.isLongPriceLayout,
      };
    },
    isPageLinkGrid(): boolean {
      return false;//!this.section.ms_group_by_price
        // && !this.section.ms_show_pricing_header
        // && !this.section.ms_show_bin;
    },
    isLongPriceLayout(): boolean {
      for (let groupTitle in this.section.grouped_items) {
        for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
          for (let i = 0, l = this.section.grouped_items[groupTitle][subgroupTitle].length || 0; i < l; i++) {
            if (this.section.grouped_items[groupTitle][subgroupTitle][i].price_display.length >= this.longPriceThreshold) {
              return true;
            }
          }
        }
      }

      return false;
    },
  },
  methods: {
    isSubgroupPricingShown: methodIsSubgroupPricingShown,
    getPreviousGroupAndSubgroupTitles: methodGetPreviousGroupAndSubgroupTitles,
  },
})
